import {
  Box,
  Button,
  CssBaseline,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ThemeProvider
} from '@mui/material';
import Typography from '@mui/material/Typography';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';
import themev5 from '../../../themev5';
import separationStyles from './separationWorklist.styles';
import DatagridFooter from '../../Datagrid/DatagridFooter';
import DatagridNoRowsOverlay from '../../Datagrid/DatagridNoRowsOverlay';
import Actions from '../../../store/actions';
import Store from '../../../store';
import Styles from '../../Canada/styles/curationdashboard.styles';
import {
  getSeparationWorklist,
  resolveSeparationWorklistResponse
} from '../../../api/separationWorklist';
import { WorklistRow, SeparationWorklistConfig } from './types';
import { resolveColumns } from './buildColumns';

/**
 * Config-driven worklist for label/section separation pipelines.
 *
 * Parent supplies SeparationWorklistConfig (API base, stage, QC options, row actions).
 * Optional refreshToken: increment after a child dialog save to refetch without remounting.
 */
type SeparationWorklistPageProps = {
  config: SeparationWorklistConfig;
  /** Bump to trigger a refetch (e.g. after saving label ranges in a dialog). */
  refreshToken?: number;
  /** Extra nodes rendered after the Search button in the toolbar (e.g. a DAG trigger button). */
  headerActions?: React.ReactNode;
};

const SeparationWorklistPage = ({
  config,
  refreshToken = 0,
  headerActions = null
}: SeparationWorklistPageProps) => {
  const { dispatch } = useContext<any>(Store);
  const classes = Styles();
  const history = useHistory();

  const [data, setData] = useState<WorklistRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageData, setPageData] = useState({ pageNo: 0, pageSize: 10 });
  const [searchInput, setSearchInput] = useState('');
  const [qcStatusInput, setQcStatusInput] = useState('All');
  /** Filters sent to the API — updated only when the user clicks Search. */
  const [appliedFilters, setAppliedFilters] = useState({ search: '', qcStatus: 'All' });
  const [totalRowCount, setTotalRowCount] = useState(0);

  const rowsPerPageOptions = [10, 20, 50, 100];
  const columns = useMemo(() => resolveColumns(config, history), [config, history]);
  const fetchRequestIdRef = useRef(0);

  /** Server-paginated fetch; ignores stale responses when filters/page change quickly. */
  const fetchData = useCallback(async () => {
    fetchRequestIdRef.current += 1;
    const requestId = fetchRequestIdRef.current;

    try {
      setIsLoading(true);
      const response = await getSeparationWorklist(config.apiBasePath, {
        stage: config.stage,
        pageSize: pageData.pageSize,
        pageNo: pageData.pageNo,
        search: appliedFilters.search.trim() || undefined,
        qcStatus: appliedFilters.qcStatus
      });

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      const result = resolveSeparationWorklistResponse(response);
      if (result.ok) {
        setData(result.data);
        setTotalRowCount(result.count);
        return;
      }

      setData([]);
      setTotalRowCount(0);
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: result.message }
      });
    } catch (error: unknown) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }

      setData([]);
      setTotalRowCount(0);
      const message = error instanceof Error ? error.message : 'Failed to fetch worklist';
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message }
      });
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    config.apiBasePath,
    config.stage,
    dispatch,
    pageData.pageNo,
    pageData.pageSize,
    appliedFilters
  ]);

  const handleSearch = () => {
    setAppliedFilters({ search: searchInput, qcStatus: qcStatusInput });
    if (pageData.pageNo !== 0) {
      setPageData(prev => ({ ...prev, pageNo: 0 }));
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshToken]);

  return (
    <ThemeProvider theme={themev5}>
      <CssBaseline />
      <Box display='flex' ml={2} mt={10} mr={2} alignItems='center'>
        <TextField
          variant='outlined'
          type='search'
          id='separation-search'
          label='Search'
          placeholder={config.searchLabel ?? 'Search'}
          value={searchInput}
          sx={separationStyles.searchField}
          InputLabelProps={{ shrink: true }}
          InputProps={{ sx: separationStyles.searchInput }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSearchInput(event.target.value);
          }}
        />

        <FormControl sx={separationStyles.qcStatusControl}>
          <InputLabel id='qc-status-label'>QC Status</InputLabel>
          <Select
            labelId='qc-status-label'
            id='qc_status'
            value={qcStatusInput}
            label='QC Status'
            sx={separationStyles.qcStatusSelect}
            onChange={(event: SelectChangeEvent) => {
              setQcStatusInput(event.target.value);
            }}>
            <MenuItem value='All'>All</MenuItem>
            {config.qcStatusOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button onClick={handleSearch} variant='contained' sx={separationStyles.searchButton}>
          <Typography>Search</Typography>
        </Button>

        {headerActions && <Box sx={separationStyles.headerActionsBox}>{headerActions}</Box>}
      </Box>

      <Box
        p={2}
        mt={1}
        display='flex'
        alignItems='center'
        justifyContent='center'
        className={classes.root}
        sx={separationStyles.dataGridContainer}>
        <DataGrid
          sx={separationStyles.dataGrid}
          style={{ height: '100%' }}
          rowSpacingType='margin'
          sortingOrder={['desc', 'asc']}
          checkboxSelection={false}
          density='standard'
          rows={data}
          rowCount={totalRowCount}
          columns={columns}
          loading={isLoading}
          getRowId={(row: WorklistRow) => row.id}
          paginationMode='server'
          getRowClassName={() => 'table-rows'}
          pageSize={pageData.pageSize}
          page={pageData.pageNo}
          onPageSizeChange={newPageSize => {
            setPageData(prev => ({ ...prev, pageSize: newPageSize }));
          }}
          onPageChange={number => {
            setPageData(prev => ({ ...prev, pageNo: number }));
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          components={{
            LoadingOverlay: LinearProgress,
            Footer: DatagridFooter,
            NoRowsOverlay: DatagridNoRowsOverlay
          }}
          pagination
        />
      </Box>
    </ThemeProvider>
  );
};

SeparationWorklistPage.defaultProps = {
  refreshToken: 0,
  headerActions: null
};

export default SeparationWorklistPage;
